import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  InsertImpactMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

export interface InsertImpactTRPCInput {
  Name: string;
  Rationale?: InputMaybe<string>;
  RatingGuidance?: InputMaybe<string>;
  LikelihoodAppetite?: InputMaybe<number>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): InsertImpactMutation => {
  return {
    insertImpactApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useInsertImpactTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.impact.insert.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impacts.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactList.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.impact.impactCount.queryKey(),
          }),
        ]);
      },
    }),
    throwOnError: true,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification, trpcEnabled]);

  return {
    insertImpact: async (
      variables: InsertImpactTRPCInput
    ): Promise<InsertImpactMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
